import {
	Building2,
	HardHat,
	Home,
	type LucideIcon,
	PaintRoller,
	User,
	Warehouse,
	Wrench,
} from "lucide-react";
import type {
	OnboardingMode,
	OnboardingProfile,
	SiteStatus,
	TeamRole,
} from "./types";

export type ProfileAccent = "primary" | "violet" | "success" | "warning";

interface ProfileOption {
	id: OnboardingProfile;
	icon: LucideIcon;
	accent: ProfileAccent;
	title: string;
	description: string;
	needsCompanyName?: boolean;
}

export const PROFILE_OPTIONS: readonly ProfileOption[] = [
	{
		id: "construction-company",
		icon: Building2,
		accent: "primary",
		title: "Construtora",
		description: "Tenho uma empresa e gerencio várias obras.",
		needsCompanyName: true,
	},
	{
		id: "independent-pro",
		icon: HardHat,
		accent: "violet",
		title: "Profissional autônomo",
		description: "Sou engenheiro(a) ou arquiteto(a) e toco obras de clientes.",
	},
	{
		id: "self-build",
		icon: Home,
		accent: "success",
		title: "Vou construir",
		description: "Estou tocando a obra da minha casa ou de um imóvel.",
	},
	{
		id: "renovation",
		icon: PaintRoller,
		accent: "warning",
		title: "Estou reformando",
		description: "Quero acompanhar uma reforma do início ao fim.",
	},
];

export function profileNeedsCompanyName(
	profile: OnboardingProfile | null | undefined,
): boolean {
	return (
		PROFILE_OPTIONS.find((option) => option.id === profile)?.needsCompanyName ??
		false
	);
}

interface ModeOption {
	id: OnboardingMode;
	icon: LucideIcon;
	accent: "primary" | "success";
	title: string;
	description: string;
	highlight: string;
}

export const MODE_OPTIONS: readonly ModeOption[] = [
	{
		id: "site-projects",
		icon: Building2,
		accent: "primary",
		title: "Canteiro com várias obras",
		description:
			"Cadastre um canteiro e adicione quantas obras quiser dentro dele.",
		highlight: "Ideal para empreendimentos com múltiplos blocos ou torres.",
	},
	{
		id: "single-project",
		icon: Home,
		accent: "success",
		title: "Vou cadastrar uma obra",
		description:
			"Cadastre direto a sua obra — o canteiro é criado automaticamente.",
		highlight: "Ideal para obras unifamiliares ou reformas pontuais.",
	},
];

export const PROJECT_TYPES = [
	"Residencial unifamiliar",
	"Residencial multifamiliar",
	"Comercial",
	"Industrial",
	"Reforma / Retrofit",
	"Infraestrutura",
] as const;

export const SITE_STATUS_OPTIONS: readonly {
	value: SiteStatus;
	label: string;
}[] = [
	{ value: "active", label: "Ativo" },
	{ value: "paused", label: "Pausado" },
];

export const PROJECT_COLORS = [
	"#e76e50",
	"#2a9d90",
	"#274754",
	"#e8c468",
	"#f4a462",
] as const;

interface RoleOption {
	id: TeamRole;
	label: string;
	description: string;
	icon: LucideIcon;
}

export const TEAM_ROLES: readonly RoleOption[] = [
	{
		id: "engineer",
		label: "Engenheiro",
		description: "Responsabilidade técnica",
		icon: HardHat,
	},
	{
		id: "almoxarife",
		label: "Almoxarife",
		description: "Estoque e movimentações",
		icon: Warehouse,
	},
	{
		id: "funcionario",
		label: "Funcionário",
		description: "Operacional de campo",
		icon: Wrench,
	},
	{
		id: "cliente",
		label: "Cliente",
		description: "Acompanhamento da obra",
		icon: User,
	},
];
